import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoneyExact, formatDate } from '../../utils/format';
import { Emo, emoText } from '../../shell/EmojiIcons';
import { pick } from './feeModel.mjs';

// 46987: 'What will happen' for the sale fee form, in the shell record-drawer anatomy (eyebrow, title, status rail, label/value
// block, lines table, hint). Every figure is the server's (POST /api/sales-fees/preview): exact money text, no client arithmetic.
const KIND_KEY = { charge: 'charge', pass_through: 'passThrough', absorb_add: 'absorbAdd', absorb_deduct: 'absorbDeduct' };
const Kv = ({ label, wide, children }) => <div style={wide ? { gridColumn: '1/-1' } : undefined}><div className="lab">{emoText(label)}</div><div className="val">{children}</div></div>;

export default function FeePreview({ data, error, loading }) {
  const { t, i18n } = useTranslation('sales');
  const L = (pair) => pick(pair, i18n.language);
  const p = (k, o) => t(`feesPanel.preview.${k}`, o);
  if (!data && !error) return loading ? <div className="sfp-preview hint" data-testid="fee-preview">{p('loading')}</div> : null;
  return (
    <div className="sfp-preview" data-testid="fee-preview" aria-live="polite">
      <div className="eyebrow">{p('eyebrow')}</div>
      {error ? <div className="errbox" role="alert" data-testid="fee-preview-error">{error}</div> : <>
        <div className="sfp-preview-title">{t(`feesPanel.i.${KIND_KEY[data.kind]}`)} · {data.kind === 'absorb_deduct' ? p('followsDelivery') : t(`feesPanel.${data.scaling}`)}</div>
        <div className="rail">
          <span className="st now">{p('now')}</span>
          {data.later.map((r, i) => <React.Fragment key={i}><span className="sep" /><span className="st">{p('delivery', { n: i + 1 })}</span></React.Fragment>)}
        </div>
        <div className="kv">
          <Kv label={p('earned')}>{data.kind === 'charge' ? p(`earned_${data.scaling}`) : data.later.length ? p('earned_proportional') : p('earned_now')}</Kv>
          <Kv label={p('order')}>{p(`nature.${data.order.nature}`)} · {formatMoneyExact(data.order.order_value)}</Kv>
          <Kv label={p('accounts')} wide>{data.accounts.from} → {data.accounts.to}</Kv>
          {data.slice_basis && <Kv label={p('basis')} wide>{L(data.slice_basis)}</Kv>}
        </div>
        <div className="sfp-preview-tbl">
          <table className="gtable zebra">
            <thead><tr><th>{p('col.when')}</th><th>{p('col.what')}</th><th>{p('col.due')}</th><th className="num">{p('col.amount')}</th></tr></thead>
            <tbody>
              {data.now.map((r, i) => (
                <tr key={`n${i}`} data-testid="fee-preview-now"><td>{formatDate(r.date)}</td>
                  <td>{L(r.label)}<div className="rec-mut">{r.from_account || p('notChosen')} → {r.to_account || p('notChosen')}</div></td>
                  <td>{r.due_date ? formatDate(r.due_date) : '—'}</td><td className="num">{formatMoneyExact(r.amount)}</td></tr>))}
              {data.later.map((r, i) => (
                <tr key={`l${i}`} data-testid="fee-preview-later"><td>{p('delivery', { n: i + 1 })} <span className="chip">{p('expected')}</span></td>
                  <td>{L(r.label)}<div className="rec-mut">{p('lineQty', { line: String(r.line_id).replace(/^.*-(L\d+)$/, '$1'), qty: r.qty })}</div></td>
                  <td>{L(r.due_rule)}</td><td className="num">{formatMoneyExact(r.amount)}</td></tr>))}
              {!data.now.length && !data.later.length && <tr><td colSpan={4}>{p('nothing')}</td></tr>}
            </tbody>
            <tfoot>
              <tr><td colSpan={3}>{p('totalNow')}</td><td className="num">{formatMoneyExact(data.total_now)}</td></tr>
              {data.later.length > 0 && <tr><td colSpan={3}>{p('totalLater')}</td><td className="num">{formatMoneyExact(data.total_later)}</td></tr>}
            </tfoot>
          </table>
        </div>
        <div className="hint"><Emo name="info" /> <span>{L(data.explanation)}</span></div>
      </>}
    </div>
  );
}
